import { type ChangeEvent, useMemo, useState } from 'react'
import { type FieldErrors, type UseFormRegister } from 'react-hook-form'

import {
  FormSelectFieldDS,
  FormTextFieldDS,
} from 'components/reusable/form/FormFields'
import { CollapsibleContainer, SolidCircleCheckIcon } from 'slab'
import { formatSSN } from 'utils'

import { type AdditionalParticipant, type ConfirmEnrollmentFormData } from '../types'

export function ConfirmEnrollmentParticipantCard ({
  coveredParticipant,
  register,
  errors,
  isFormSubmitting,
  isEverythingValid,
  genderOptions,
  validateUniqueSSN,
}: {
  coveredParticipant: AdditionalParticipant;
  register: UseFormRegister<ConfirmEnrollmentFormData>;
  errors: FieldErrors<ConfirmEnrollmentFormData>;
  isFormSubmitting: boolean;
  isEverythingValid: boolean;
  genderOptions: { value: string; label: string }[];
  validateUniqueSSN?: (value: string) => string | boolean;
}) {
  const [open, setOpen] = useState<boolean>(true)

  const heading = useMemo(() => {
    return `${coveredParticipant.given_name} ${coveredParticipant.family_name} (${coveredParticipant.relationship})`
  }, [coveredParticipant])

  return (
    <CollapsibleContainer
      open={open}
      onOpenChange={setOpen}
      heading={heading}
      headingIconContent={
        isEverythingValid && (
          <div className='text-green-400'>
            <SolidCircleCheckIcon aria-label='(complete)' size={16} />
          </div>
        )
      }
    >
      <div className='p-4 grid grid-cols-2 gap-x-4'>
        <FormSelectFieldDS<ConfirmEnrollmentFormData>
          fieldId={`dependent_${coveredParticipant.id}.gender`}
          fieldLabel='Gender'
          fieldOptions={genderOptions}
          description={
            genderOptions.length === 3
              ? "We'll use the gender you specify wherever possible, but are limited to the options that insurance companies can usually accept."
              : "While we recognize that gender is a spectrum, some insurance companies' systems require that we use binary sex."
          }
          register={register}
          placeholder='Select an option'
          error={errors[`dependent_${coveredParticipant.id}`]?.gender}
          disabled={isFormSubmitting}
          validationRule={{
            required: 'Gender is required',
          }}
        />
        <FormTextFieldDS<ConfirmEnrollmentFormData>
          fieldId={`dependent_${coveredParticipant.id}.ssn`}
          fieldLabel='SSN or ITIN'
          register={register}
          placeholder='XXX-XX-XXXX'
          error={errors[`dependent_${coveredParticipant.id}`]?.ssn}
          disabled={isFormSubmitting}
          validationRule={{
            required: 'SSN or ITIN is required',
            pattern: {
              value: /^\d{3}-?\d{2}-?\d{4}$/,
              message: 'Please enter a valid SSN or ITIN (XXX-XX-XXXX)',
            },
            validate: validateUniqueSSN, // Note: This has the side effect of calling trigger() on other SSN fields
            onChange: (e: ChangeEvent<HTMLInputElement>) => {
              e.target.value = formatSSN(e.target.value)
            },
          }}
        />
      </div>
    </CollapsibleContainer>
  )
}
